import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { AIOT_PATH, errText, verifyLabCert } from "@/lib/aiot";

// Public page opened from the QR code on an AIoT lab certificate.
export default function VerifyLabCert() {
  const { L } = useLang();
  const { code } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    verifyLabCert(code).then(setR).catch((e) => setErr(errText(e, L("No AIoT lab certificate with this number", "इस नंबर का कोई AIoT लैब प्रमाणपत्र नहीं"))));
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = r && [[L("Name", "नाम"), r.student_name || "—", "name"], [L("Certificate no.", "प्रमाणपत्र नं."), r.cert_no, "no"],
    [L("Lab", "लैब"), L(r.lab.title, r.lab.title_hi), "lab"], [L("Score", "स्कोर"), `${r.score}/100`, "score"],
    [L("Date", "तारीख"), new Date(r.issued_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }), "date"]];
  return (
    <div className="min-h-[75vh] bg-[#F3F8FB] grid place-items-center px-5 py-14 text-[#0B1F3A]" data-testid="verify-aiot-page">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_30px_80px_-50px_rgba(11,31,58,0.45)]">
        {!r && !err && <Loader2 className="w-8 h-8 animate-spin text-[#0F766E] mx-auto" data-testid="verify-aiot-loading" />}
        {err && (
          <div className="text-center" data-testid="verify-aiot-invalid">
            <ShieldX className="w-14 h-14 text-[#DC2626] mx-auto" />
            <h1 className="mt-3 text-2xl font-extrabold">{L("Not verified", "सत्यापित नहीं")}</h1>
            <p className="text-slate-500 text-sm mt-1">{err}</p>
          </div>
        )}
        {r && (
          <div data-testid="verify-aiot-result">
            <div className="text-center">
              <ShieldCheck className="w-14 h-14 text-[#059669] mx-auto" />
              <h1 className="mt-3 text-2xl font-extrabold" data-testid="verify-aiot-status">{L("Verified 3D lab certificate", "सत्यापित 3D लैब प्रमाणपत्र")}</h1>
              <p className="mt-2 text-sm text-slate-500">{L("This student scored a perfect 100/100 in a RoboSpark Advanced AIoT Diploma 3D lab. Placement, wiring and every scenario were checked on the server.", "इस छात्र ने RoboSpark एडवांस्ड AIoT डिप्लोमा की 3D लैब में 100/100 स्कोर किया। जगह, वायरिंग और हर सीनारियो सर्वर पर जाँचे गए।")}</p>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {rows.map(([k, v, id]) => (
                <div key={id} className={id === "lab" || id === "name" ? "col-span-2" : ""}><dt className="text-[11px] uppercase tracking-widest text-slate-500">{k}</dt><dd className="font-bold" data-testid={`verify-aiot-${id}`}>{v}</dd></div>
              ))}
            </dl>
            <Link to={AIOT_PATH} className="mt-6 block text-center text-sm font-semibold text-[#0F766E] hover:text-[#0B1F3A]" data-testid="verify-aiot-course-link">{L("About the Advanced AIoT Diploma →", "एडवांस्ड AIoT डिप्लोमा के बारे में →")}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
